import type { AwardTypeCode, SectorDelegate } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { sectorSetupQuery } from '../api/settings';
import { initialsOf } from '../shell';
import { Card, cx, EmptyState, Icon, StatusPill } from '../ui';
import { AWARD_TYPE_LABEL, AWARD_TYPE_SHORT } from './labels';
import type { SectorPanelModel } from './model';
import s from './Home.module.css';

/** Sector setup (SL9): a Sector Lead's delegates and the sector's programs. */
function setupHref(sectorId: string): string {
  return `/settings/sector?sector=${encodeURIComponent(sectorId)}`;
}

function listWords(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;
}

/** D1's tiles: who each type is for and what it pays, in the board's words. */
const START: Record<
  AwardTypeCode,
  { who: string; what: (sector: string) => string; edge: string }
> = {
  SDB: {
    who: 'Any size team',
    what: (sector) => `Cash, options or both for each person, from the ${sector} pool.`,
    edge: s.startSdb,
  },
  PAA: {
    who: '1–5 people',
    what: () =>
      'An amount for each person, paid in cash with options on top, and a write-up. The Executives decide.',
    edge: s.startPaa,
  },
  TAA: {
    who: '6 or more people',
    what: () => 'Cash only, with a write-up. The Executives decide.',
    edge: s.startTaa,
  },
  EDB: {
    who: 'One or more people',
    what: () => 'Cash, options or both, from the Executive pool.',
    edge: s.startSdb,
  },
};

/**
 * D1 · "Start an award for Graham" (APP-054 decision 20): the award types this delegate enters for
 * their Sector Lead, each a tile that starts one, and whether the Lead approves first. HOME1 has
 * no Recognize card (decision 17); a delegate's Home does, as its board draws it.
 */
export function StartAwardCard({ panel }: { panel: SectorPanelModel }): ReactElement | null {
  const lead = panel.forPerson;
  if (panel.role !== 'delegate' || lead === null) return null;
  const first = lead.name.split(' ')[0] ?? lead.name;
  const approves = panel.needsApproval === true;
  if (panel.offers.length === 0)
    // No types to start right now: the card stays and says why, rather than vanishing (D1).
    return (
      <Card
        title={`Start an award for ${first}`}
        help={{
          shows: `The award types you can enter for ${lead.name}, paid from the ${panel.sector.name} pool.`,
          canDo: `Nothing to start right now. ${first} chooses your award types in Sector setup.`,
        }}
      >
        <EmptyState
          variant="first-day"
          art="award"
          size="sm"
          headline="No award types to start yet"
          body={`${lead.name} chooses which award types you enter for ${panel.sector.name}. Each one shows here as a tile to start, once it’s on for you.`}
        />
      </Card>
    );
  return (
    <Card
      title={`Start an award for ${first}`}
      help={{
        shows: `The award types you can enter for ${lead.name}, paid from the ${panel.sector.name} pool.`,
        canDo: approves
          ? `Pick a type to start. ${first} approves each entry first, and is emailed each time.`
          : `Pick a type to start. Your entries go straight through, and ${first} is emailed each time.`,
      }}
    >
      <p className={s.startRoute}>
        <Icon name="mail" size={18} className={s.startRouteIcon} />
        {approves
          ? `${first} approves each entry first, and is emailed every time.`
          : `Your entries go straight through — ${first} is emailed every time.`}
      </p>
      <ul className={s.startTiles} aria-label={`Award types you can start for ${lead.name}`}>
        {panel.offers.map((offer) => {
          const tile = START[offer.type];
          return (
            <li key={offer.type}>
              <Link
                href={`/recognize?type=${offer.type}&sector=${encodeURIComponent(panel.sector.id)}`}
                className={cx(s.startTile, tile.edge)}
              >
                <span className={cx('eyebrow', s.startWho)}>{tile.who}</span>
                <span className={cx('disp', s.startName)}>{AWARD_TYPE_LABEL[offer.type]}</span>
                <span className={s.startWhat}>{tile.what(panel.sector.name)}</span>
                <span className={s.startGo}>
                  Start
                  <Icon name="arrowRight" size={14} />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * D1 · "You're a delegate for": whose awards this person enters, the award types they may use,
 * and whether the Sector Lead approves their entries first. Read-only: the Sector Lead changes it
 * in Sector setup. From the person's own role (Recognize options), never another read.
 */
export function DelegateCard({ panel }: { panel: SectorPanelModel }): ReactElement | null {
  const lead = panel.forPerson;
  if (panel.role !== 'delegate' || lead === null) return null;
  const types = panel.offers.map((offer) => AWARD_TYPE_SHORT[offer.type]);
  return (
    <Card
      title="You’re a delegate for"
      titleSize="sm"
      compact
      tone="dark"
      help={{
        shows: `Whose awards you enter, the award types you can use, and whether ${lead.name} approves your entries first.`,
        canDo: `Read only. ${lead.name} changes this in Sector setup.`,
      }}
    >
      <div className={s.person}>
        <span className={s.personAvatar} aria-hidden="true">
          {initialsOf(lead.name)}
        </span>
        <span className={s.personText}>
          <span className={s.personName}>{lead.name}</span>
          <span className={s.personRole}>{`Sector Lead · ${panel.sector.name}`}</span>
        </span>
      </div>
      <dl className={s.delegateFacts}>
        <div className={s.delegateFact}>
          <dt className={s.onDarkMeta}>Award types</dt>
          <dd>
            {types.length > 0
              ? listWords(types)
              : `None you can start right now. ${lead.name} chooses them in Sector setup.`}
          </dd>
        </div>
        <div className={s.delegateFact}>
          <dt className={s.onDarkMeta}>{`${lead.name} approves first`}</dt>
          <dd>{panel.needsApproval === true ? 'Yes' : 'No'}</dd>
        </div>
      </dl>
      {/* PRD §12: records say "by [delegate] for [Sector Lead]"; letters are still signed by the
          principal. Over-budget awards go to the Executives as any other. */}
      <p className={s.onDarkMeta}>
        {`Letters are still signed by ${lead.name}. Over-budget awards go to the Executives.`}
      </p>
    </Card>
  );
}

function delegateNote(d: SectorDelegate): string {
  if (!d.active) return 'Turned off';
  return d.needsApproval ? 'Needs my approval' : 'Straight through';
}

/**
 * SL1 / FR2 · "Your delegates": who can enter awards for the Sector Lead, and whether each one's
 * entries need their OK. Managed in Sector setup; the Lead is always emailed about every entry.
 */
export function YourDelegatesCard({ panel }: { panel: SectorPanelModel }): ReactElement | null {
  const setup = useQuery({ ...sectorSetupQuery(panel.sector.id), retry: false });
  if (panel.role !== 'sector_lead' || setup.isError) return null;
  // `null`: this reader may see the programs but not the delegates (not this sector's Lead).
  const delegates = setup.data?.delegates;
  if (delegates === null) return null;
  const href = setupHref(panel.sector.id);
  return (
    <Card
      title="Your delegates"
      titleSize="sm"
      compact
      tone="dark"
      help={{
        shows:
          'Who can enter awards for you, and whether their entries need your OK. You’re always emailed about every entry.',
        canDo:
          'Add or remove delegates, and turn “needs my approval” on or off for each person, in Sector setup.',
      }}
    >
      {delegates === undefined ? (
        <p className={s.onDarkMeta}>Loading your delegates…</p>
      ) : delegates.length === 0 ? (
        <>
          <p className={s.onDarkText}>
            No delegates yet. A delegate can enter awards for you; you’re always emailed when they
            do.
          </p>
          <Link href={href} className={s.onDarkLink}>
            Add a delegate
          </Link>
        </>
      ) : (
        <>
          <ul className={s.leadList} aria-label="Your delegates">
            {delegates.map((d) => (
              <li key={d.person.id} className={s.leadRow}>
                <span className={s.leadName}>{d.person.name}</span>
                <StatusPill
                  tone={!d.active ? 'neutral' : d.needsApproval ? 'waiting' : 'info'}
                  size="sm"
                  label={delegateNote(d)}
                />
              </li>
            ))}
          </ul>
          <Link href={href} className={s.onDarkLink}>
            Manage delegates and programs
          </Link>
        </>
      )}
    </Card>
  );
}
